import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { LogoMarkOnWhite } from './Logo';
import type { Language } from '../types/content';
const ease = [0.22, 1, 0.36, 1] as const;
const options: {
  code: Language;
  flag: string;
  label: string;
  noteKey: 'englishNote' | 'spanishNote';
}[] = [{
  code: 'en',
  flag: '🇺🇸',
  label: 'English',
  noteKey: 'englishNote'
}, {
  code: 'es',
  flag: '🇪🇸',
  label: 'Español',
  noteKey: 'spanishNote'
}];
export function LanguageGate({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    hasChosen,
    setLanguage,
    t
  } = useLanguage();
  React.useEffect(() => {
    document.body.style.overflow = hasChosen ? '' : 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [hasChosen]);
  return <>
      <AnimatePresence data-id="element-3" data-mp="180a4jncg4ojr">
        {!hasChosen && <motion.div key="language-gate" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5,
        ease
      }} className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-ink px-5 py-10" role="dialog" aria-modal="true" aria-label={t.switcher.label} data-id="element-4" data-mp="183fqwutoxcbs">
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[680px] w-[680px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/[0.16] blur-3xl" aria-hidden="true" data-id="element-5" data-mp="1lcj97iqk664w" />
            <motion.div initial={{
          opacity: 0,
          y: 24,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 16,
          scale: 0.98
        }} transition={{
          duration: 0.6,
          delay: 0.08,
          ease
        }} className="glass-dark relative w-full max-w-[640px] rounded-4xl border border-white/12 px-6 py-12 text-center shadow-lift sm:px-14 sm:py-16" data-id="element-6" data-mp="1bmg9z0sufvy4">
              <div className="flex justify-center" data-id="element-7" data-mp="izyoti1x9sqm">
                <span className="rounded-3xl bg-white px-7 py-5 shadow-lift" data-id="element-8" data-mp="nzutljig1o78">
                  <LogoMarkOnWhite width={200} data-id="element-9" data-mp="nxrtg6nkueos" />
                </span>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-2" data-id="element-10" data-mp="94ex77ue5g7o">
                {options.map((option, i) => <motion.button key={option.code} type="button" onClick={() => setLanguage(option.code)} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2 + i * 0.08,
              ease
            }} className="group flex items-center gap-4 rounded-3xl border border-white/12 bg-white/[0.06] p-6 text-left backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-brand hover:bg-white hover:shadow-lift" data-id="element-11" data-mp="e1x7b1k5a1h9" data-mpi={i}>
                    <span className="text-[30px] leading-none" aria-hidden="true" data-id="element-12" data-mp="1piera26k2bn0">
                      {option.flag}
                    </span>
                    <span className="flex flex-1 flex-col" data-id="element-13" data-mp="3585zz6ontmo4">
                      <span className="text-[18px] font-semibold tracking-tight text-white transition-colors duration-300 group-hover:text-ink" data-id="element-14" data-mp="357w5b5so6rc3">
                        {option.label}
                      </span>
                      <span className="mt-1 text-[13px] text-white/55 transition-colors duration-300 group-hover:text-ink/55" data-id="element-15" data-mp="3f026n64tal3y">
                        {t.gate[option.noteKey]}
                      </span>
                    </span>
                    <ArrowRightIcon className="h-5 w-5 shrink-0 text-white/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" aria-hidden="true" data-id="element-16" data-mp="308tdin1pahm2" />
                  </motion.button>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {hasChosen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.6,
      delay: 0.15,
      ease
    }} data-id="element-17" data-mp="bhs7w16e04jl">
          {children}
        </motion.div>}
    </>;
}